// src/pages/etc/InsuranceAllocation.jsx
// 사회보험 부문 구분 — 연월별 매핑/순서 + db가져오기 + 초기화 + 부문/소속 DB + 검색 + 엑셀
import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import api from "../../api";

// 부문 기본값(폴백). 실제 목록은 DB(business-units)에서 로드
const DEFAULT_UNITS = [
  { key: "경영팀", label: "경영팀(본사)" },
  { key: "용역", label: "용역사업" },
  { key: "환경/소독", label: "환경/소독" },
  { key: "환경/종합", label: "환경/종합" },
  { key: "카페_가산", label: "카페인 가산점" },
  { key: "카페_독산", label: "카페인 독산점" },
  { key: "카페_금나래", label: "카페인 금나래점" },
  { key: "카페_그린", label: "카페인 그린점" },
];

const SOURCES = ["전체", "건강+요양", "국민연금", "고용", "산재"];

const INS_ORDER = ["건강", "요양", "국민연금", "고용_실업", "고용_고안", "산재"];
const INS_LABEL = { 건강: "건강", 요양: "요양", 국민연금: "국민", 고용_실업: "고용(실업)", 고용_고안: "고용(고안)", 산재: "산재" };
const INS_LABEL_FULL = { 건강: "건강", 요양: "요양", 국민연금: "국민연금", 고용_실업: "고용(실업급여)", 고용_고안: "고용(고안직능)", 산재: "산재" };
const SOURCE_TYPES = {
  전체: INS_ORDER,
  국민연금: ["국민연금"],
  "건강+요양": ["건강", "요양"],
  고용: ["고용_실업", "고용_고안"],
  산재: ["산재"],
};

// 메인테이블에서 단독으로 보일 소속. 이 외(미지정 제외)는 "현장근로자"로 합산
const KEEP_AFFS = ["본사", "종합환경", "소독방역"];
const FIELD_LABEL = "현장근로자";


// 연월 드롭다운용
const NOW_Y = new Date().getFullYear();
const YEARS = [];
for (let y = NOW_Y + 1; y >= 2024; y--) YEARS.push(y);
const MONTHS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));

const num = (v) => { const n = Number(String(v ?? "0").replace(/[, 원]/g, "")); return Number.isFinite(n) ? n : 0; };
const fmt = (n) => Number(n || 0).toLocaleString();
const normName = (s) => String(s ?? "").replace(/\s+/g, "").trim();
const normBirth = (v) => String(v ?? "").replace(/\D/g, "").slice(0, 6);
const isNumeric = (v) => v !== "" && v !== null && v !== undefined && !isNaN(Number(v));
const pkey = (name, birth) => `${name}|${birth}`;
const HANGUL_NAME = /^[가-힣]{2,4}$/;
const fmtYm = (s) => (s && s.length >= 7 ? `${s.slice(0, 4)}년 ${+s.slice(5, 7)}월` : s);

// ─── 파서 ──
const parseNps = (rows) => {
  let cName = -1, cRrn = -1, cTotal = -1, cEmp = -1;
  for (const r of rows) {
    const i = r.findIndex((c) => String(c).includes("성명"));
    if (i === -1) continue;
    cName = i;
    cRrn = r.findIndex((c) => String(c).includes("주민"));
    cTotal = r.findIndex((c) => String(c).includes("연금보험료"));
    cEmp = r.findIndex((c) => String(c).includes("사용자부담금"));
    break;
  }
  const out = [];
  if (cName === -1) return out;
  for (const r of rows) {
    const name = normName(r[cName]); const rrn = String(r[cRrn] ?? "");
    if (name && name !== "성명" && /\*{3,}|^\d{6}-/.test(rrn)) {
      out.push({ ins_type: "국민연금", name, birth: normBirth(rrn), total: num(r[cTotal]), employer: num(r[cEmp]) });
    }
  }
  return out;
};
const parseNhis = (rows) => {
  const out = [];
  for (const r of rows) {
    const name = normName(r[3]); const rrn = String(r[2] ?? "");
    if (isNumeric(r[0]) && name && name !== "성명") {
      const birth = normBirth(rrn); const h = num(r[13]); const c = num(r[26]);
      out.push({ ins_type: "건강", name, birth, total: h * 2, employer: h });
      out.push({ ins_type: "요양", name, birth, total: c * 2, employer: c });
    }
  }
  return out;
};
const parseEmployment = (rows) => {
  const out = [];
  for (const r of rows) {
    const name = normName(r[2]);
    if (isNumeric(r[0]) && name && name !== "근로자명") {
      const last = r.length - 1;
      const birth = normBirth(r[3]);
      const workerUi = num(r[last - 3]); // W 근로자실업급여보험료
      const empUi = num(r[last - 2]); // X 사업주실업급여보험료
      const empAnjik = num(r[last - 1]); // Y 사업주고안직능보험료
      out.push({ ins_type: "고용_실업", name, birth, total: workerUi + empUi, employer: empUi });
      out.push({ ins_type: "고용_고안", name, birth, total: empAnjik, employer: empAnjik });
    }
  }
  return out;
};

// 고용 파일에서 사람별 근로구분/퇴직여부 추출
//  B열(인덱스1) 근로자구분에 "일용" 포함 → 일용직, G열(인덱스6) 고용종료일에 값 있으면 → 퇴직
const parseEmploymentMeta = (rows) => {
  const out = [];
  for (const r of rows) {
    const name = normName(r[2]);
    if (isNumeric(r[0]) && name && name !== "근로자명") {
      const gubun = String(r[1] ?? "");
      const endDate = String(r[6] ?? "").trim();
      out.push({
        name, birth: normBirth(r[3]),
        worker_type: gubun.includes("일용") ? "일용" : "일반",
        resigned: endDate !== "" && endDate.toLowerCase() !== "none",
      });
    }
  }
  return out;
};
const parseInjury = (rows) => {
  const out = [];
  for (const r of rows) {
    const name = normName(r[2]);
    if (isNumeric(r[0]) && name && name !== "근로자명") {
      const total = num(r[r.length - 1]);
      out.push({ ins_type: "산재", name, birth: normBirth(r[3]), total, employer: total });
    }
  }
  return out;
};
const detectParse = (rows) => {
  const flat = rows.flat().map((c) => String(c)).join("|");
  if (/연금보험료/.test(flat)) return parseNps(rows);
  if (/고지보험료/.test(flat) || (/건강/.test(flat) && /요양/.test(flat))) return parseNhis(rows);
  if (/실업/.test(flat)) return parseEmployment(rows);
  if (/근로자\s*구분|근로자명/.test(flat)) return parseInjury(rows);
  return [];
};
const detectYm = (rows, filename) => {
  const flat = rows.flat().map((c) => String(c)).join(" ");
  let m = flat.match(/(20\d{2})\s*년\s*(\d{1,2})\s*월/);
  if (m) return `${m[1]}-${String(m[2]).padStart(2, "0")}`;
  const f = String(filename || "");
  m = f.match(/(20\d{2})\D{0,3}(\d{1,2})\s*월/);
  if (m) return `${m[1]}-${String(m[2]).padStart(2, "0")}`;
  m = f.match(/(20\d{2})[-_.](\d{1,2})(?!\d)/);
  if (m && +m[2] >= 1 && +m[2] <= 12) return `${m[1]}-${String(m[2]).padStart(2, "0")}`;
  return null;
};

const parseNameOrder = (rows) => {
  // 1) 이름 열 찾기 (기존과 동일)
  let nameCol = -1, headerRow = -1;
  for (let i = 0; i < rows.length; i++) {
    const j = rows[i].findIndex((c) => /이름|성명|성\s*명/.test(String(c)));
    if (j !== -1) { nameCol = j; headerRow = i; break; }
  }
  // 헤더를 못 찾으면 한글이름이 가장 많은 열을 이름 열로 (기존 폴백)
  if (nameCol === -1) {
    const colCount = {};
    rows.forEach((r) => r.forEach((c, idx) => { if (HANGUL_NAME.test(normName(c))) colCount[idx] = (colCount[idx] || 0) + 1; }));
    const best = Object.entries(colCount).sort((a, b) => b[1] - a[1])[0];
    if (!best) return [];
    nameCol = Number(best[0]);
    headerRow = -1;
  }

  // 2) 등장 순서대로 모두 수집 (중복 유지 — 인사이동자는 두 위치 모두 보존)
  //    명단은 '현재 소속'을 더 아래쪽에 적는 규칙. 정렬은 orderIndex에서 lastIndexOf로 처리.
  const start = headerRow === -1 ? 0 : headerRow + 1;
  const out = [];
  for (let i = start; i < rows.length; i++) {
    const nm = normName(rows[i][nameCol]);
    if (HANGUL_NAME.test(nm)) out.push(nm);
  }
  return out;
};



const readRows = async (file) => {
  const isCsv = /\.csv$/i.test(file.name);
  let wb;
  if (isCsv) {
    const buf = await file.arrayBuffer();
    let text;
    try { text = new TextDecoder("euc-kr").decode(buf); } catch { text = new TextDecoder("utf-8").decode(buf); }
    wb = XLSX.read(text, { type: "string" });
  } else {
    const buf = await file.arrayBuffer();
    wb = XLSX.read(buf, { type: "array" });
  }
  const ws = wb.Sheets[wb.SheetNames[0]];
  return XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: "" });
};

export default function InsuranceAllocation() {
  const [ym, setYm] = useState("");
  const [basis, setBasis] = useState("total");
  const [source, setSource] = useState("전체");

  const [people, setPeople] = useState([]);
  const [mapping, setMapping] = useState({});   // pkey -> unit
  const [affMap, setAffMap] = useState({});     // pkey -> affiliation
  const [nameOrder, setNameOrder] = useState([]);

  const [summaryEmp, setSummaryEmp] = useState(null); // 사업주부담(기관)
  const [summaryTot, setSummaryTot] = useState(null); // 총액
  const [months, setMonths] = useState([]);

  // 마스터/연월목록
  const [units, setUnits] = useState(DEFAULT_UNITS);
  const [affs, setAffs] = useState([]);
  const [mappingMonths, setMappingMonths] = useState([]);
  const [orderMonths, setOrderMonths] = useState([]);

  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [showModal, setShowModal] = useState(false);       // 개별
  const [showMapModal, setShowMapModal] = useState(false); // 매핑 설정
  const [showOrderModal, setShowOrderModal] = useState(false); // 순서 설정
  const [query, setQuery] = useState("");
  const [empTab, setEmpTab] = useState("전체"); // 전체/재직자/퇴직자/일용직


  const [mapQuery, setMapQuery] = useState("");
  const [interest, setInterest] = useState({}); // 보험종류(t) -> 이자 차감액(문자열 입력)

  const unitLabelMap = Object.fromEntries(units.map((u) => [u.key, u.label]));

  // ── 로더 ──
  const loadPeople = async (t = ym) => {
    try {
      const res = await api.get("/insurance/people/", { params: { ym: t } });
      const list = Array.isArray(res.data) ? res.data : [];
      setPeople(list);
      const m = {}; const a = {};
      list.forEach((p) => {
        const k = pkey(p.name, p.birth);
        if (p.unit) m[k] = p.unit;
        if (p.affiliation) a[k] = p.affiliation;
      });
      setMapping(m); setAffMap(a);
    } catch (e) { setPeople([]); setMapping({}); setAffMap({}); }
  };

  const loadSummary = async (t = ym) => {
    try {
      const [e, tot] = await Promise.all([
        api.get("/insurance/summary/", { params: { ym: t, basis: "employer", group: "affiliation" } }),
        api.get("/insurance/summary/", { params: { ym: t, basis: "total", group: "affiliation" } }),
      ]);
      setSummaryEmp(e.data); setSummaryTot(tot.data);
    } catch (err) { setSummaryEmp(null); setSummaryTot(null); }
  };



  const loadMonths = async () => {
    try { const r = await api.get("/insurance/months/"); setMonths(Array.isArray(r.data) ? r.data : []); } catch (e) { }
  };
  const loadNameOrder = async (t = ym) => {
    if (!t) { setNameOrder([]); return; }
    try {
      const r = await api.get("/insurance/name-order/", { params: { ym: t } });
      const arr = Array.isArray(r.data) ? r.data : (r.data?.names || []);
      setNameOrder(arr.map(normName));
    } catch (e) { setNameOrder([]); }
  };
  const loadUnits = async () => {
    try { const r = await api.get("/insurance/business-units/"); if (Array.isArray(r.data) && r.data.length) setUnits(r.data); } catch (e) { }
  };
  const loadAffs = async () => {
    try { const r = await api.get("/insurance/affiliations/"); setAffs(Array.isArray(r.data) ? r.data : []); } catch (e) { }
  };
  const loadMappingMonths = async () => {
    try { const r = await api.get("/insurance/mapping-months/"); setMappingMonths(Array.isArray(r.data) ? r.data : []); } catch (e) { }
  };


  const loadOrderMonths = async () => {
    try { const r = await api.get("/insurance/order-months/"); setOrderMonths(Array.isArray(r.data) ? r.data : []); } catch (e) { }
  };

  const loadInterest = async (t = ym) => {
    if (!t) { setInterest({}); return; }
    try {
      const r = await api.get("/insurance/interest/", { params: { ym: t } });
      const obj = r.data && typeof r.data === "object" ? r.data : {};
      // DB는 보험종류별. 화면은 "고용" 한 칸이라 고용_실업+고용_고안을 합쳐서 넣음.
      const next = {};
      Object.entries(obj).forEach(([k, v]) => {
        if (k === "고용_실업" || k === "고용_고안") {
          next["고용"] = String(num(next["고용"]) + num(v));
        } else {
          next[k] = String(v);
        }
      });
      setInterest(next);
    } catch (e) { setInterest({}); }
  };


  const saveInterest = async () => {
    if (!ym) { setMsg("연월을 먼저 선택하세요."); return; }
    setBusy(true);
    try {
      // 이자는 메인테이블 열(col.key) 기준 입력. DB는 보험종류별이라
      // "고용"은 고용_실업에 몰아 저장(고용_고안=0). 나머지는 그대로.
      const items = {};
      INS_ORDER.forEach((t) => { items[t] = 0; });
      Object.entries(interest).forEach(([key, val]) => {
        const amt = num(val);
        if (key === "고용") items["고용_실업"] = amt;
        else if (INS_ORDER.includes(key)) items[key] = amt;
      });
      await api.post("/insurance/interest/", { year_month: ym, items });
      setMsg(`${fmtYm(ym)} 이자 차감 저장 완료.`);
    } catch (err) { setMsg("이자 차감 저장 실패: " + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };


  useEffect(() => { loadMonths(); loadUnits(); loadAffs(); loadMappingMonths(); loadOrderMonths(); }, []);




  useEffect(() => { if (ym) { loadPeople(ym); loadSummary(ym); loadNameOrder(ym); loadInterest(ym); } /* eslint-disable-next-line */ }, [ym]);



  // ── 정렬 ──
  const orderIndex = (name) => {
    const i = nameOrder.lastIndexOf(normName(name));
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };


  const sortByOrder = (arr) =>
    arr.slice().sort((a, b) => {
      const d = orderIndex(a.name) - orderIndex(b.name);
      return d !== 0 ? d : String(a.name).localeCompare(String(b.name), "ko");
    });

  // ── 명세 업로드/삭제 ──
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    if (!ym) { setMsg("연월을 먼저 선택하세요."); return; }
    setMsg(`${file.name} 읽는 중...`); setBusy(true);
    try {
      const rows = await readRows(file);
      let recs = [];
      if (source === "전체") recs = detectParse(rows);
      else if (source === "국민연금") recs = parseNps(rows);
      else if (source === "건강+요양") recs = parseNhis(rows);
      else if (source === "고용") recs = parseEmployment(rows);
      else if (source === "산재") recs = parseInjury(rows);
      if (recs.length === 0) {
        setMsg(`인식된 데이터가 없어요. (종류: ${source}) 파일/종류를 확인해 보세요.`);
        setBusy(false); return;
      }
      // 고용 파일이면 근로구분/퇴직 meta 동봉 (자동인식 포함)
      const isEmployment = recs.some((x) => x.ins_type === "고용_실업" || x.ins_type === "고용_고안");
      const meta = isEmployment ? parseEmploymentMeta(rows) : [];
      const selectedYm = ym;
      const detected = detectYm(rows, file.name);
      let targetYm = ym; let note = "";
      if (detected && detected !== selectedYm) {
        targetYm = detected; setYm(detected);
        note = ` ⚠ 파일에서 ${detected} 감지 → ${detected}로 저장`;
      } else if (detected) { note = ` (연월 ${detected} 확인)`; }
      else { note = ` (연월 자동감지 실패 → ${selectedYm}에 저장)`; }
      const res = await api.post("/insurance/premiums/upload/", { year_month: targetYm, records: recs, meta });
      setMsg(`[${targetYm}] ${res.data?.types?.join(", ")} ${res.data?.saved}건 저장 완료.${meta.length ? ` · 근로구분 ${meta.length}명 반영` : ""}${note}`);
      await loadPeople(targetYm); await loadSummary(targetYm); await loadMonths();
    } catch (err) {
      setMsg("업로드/저장 실패: " + (err.response?.data?.detail || err.message));
    } finally { setBusy(false); }
  };

  const deleteMonth = async () => {
    if (!ym) return;
    if (!window.confirm(`${fmtYm(ym)} 의 사회보험 명세를 모두 삭제할까요?\n(되돌릴 수 없습니다)`)) return;
    setBusy(true);
    try {
      const res = await api.post("/insurance/premiums/delete/", { ym });
      setMsg(`${fmtYm(ym)} 명세 ${res.data?.deleted}건 삭제 완료.`);
      await loadPeople(ym); await loadSummary(ym); await loadMonths();
    } catch (err) { setMsg("삭제 실패: " + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };

  // ── 매핑 ──
  const setUnit = (k, v) => setMapping((p) => { const n = { ...p }; if (v) n[k] = v; else delete n[k]; return n; });
  const setAff = (k, v) => setAffMap((p) => { const n = { ...p }; if (v) n[k] = v; else delete n[k]; return n; });

  const saveMapping = async () => {
    if (!ym) { setMsg("연월을 먼저 선택하세요."); return; }
    const items = people
      .filter((p) => { const k = pkey(p.name, p.birth); return mapping[k] || affMap[k]; })
      .map((p) => { const k = pkey(p.name, p.birth); return { name: p.name, birth: p.birth, unit: mapping[k] || "", affiliation: affMap[k] || "" }; });
    if (items.length === 0) { setMsg("저장할 매핑이 없어요."); return; }
    setBusy(true);
    try {
      const res = await api.post("/insurance/employee-units/bulk/", { year_month: ym, items });
      setMsg(`${fmtYm(ym)} 매핑 ${res.data?.saved ?? items.length}건 저장 완료.`);
      await loadPeople(ym); await loadSummary(ym); await loadMappingMonths();
    } catch (err) { setMsg("매핑 저장 실패: " + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };

  const importMapping = async (srcYm) => {
    if (!srcYm) return;
    setBusy(true);
    try {
      const r = await api.get("/insurance/people/", { params: { ym: srcYm } });
      const src = {};
      (Array.isArray(r.data) ? r.data : []).forEach((p) => { src[pkey(p.name, p.birth)] = { unit: p.unit, affiliation: p.affiliation }; });
      const m = { ...mapping }, a = { ...affMap };
      people.forEach((p) => {
        const k = pkey(p.name, p.birth); const s = src[k];
        if (s) { if (s.unit) m[k] = s.unit; if (s.affiliation) a[k] = s.affiliation; }
      });
      setMapping(m); setAffMap(a);
      setMsg(`${fmtYm(srcYm)} 매핑을 불러왔습니다. 신규자는 미지정 — 확인 후 [저장]을 누르세요.`);
    } catch (err) { setMsg("매핑 가져오기 실패: " + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };

  const resetMapping = async () => {
    if (!ym) return;
    if (!window.confirm(`${fmtYm(ym)} 매핑을 초기화 하시겠습니까?\n(해당 월 배정부문·소속이 모두 삭제됩니다)`)) return;
    setBusy(true);
    try {
      const res = await api.post("/insurance/mapping/reset/", { ym });
      setMsg(`${fmtYm(ym)} 매핑 ${res.data?.deleted ?? 0}건 초기화 완료.`);
      await loadPeople(ym); await loadSummary(ym); await loadMappingMonths();
    } catch (err) { setMsg("매핑 초기화 실패: " + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };

  // ── 순서 ──
  const handleOrderFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    if (!ym) { setMsg("연월을 먼저 선택하세요."); return; }
    setBusy(true); setMsg(`${file.name} 읽는 중...`);
    try {
      const rows = await readRows(file);
      const names = parseNameOrder(rows);
      if (names.length === 0) { setMsg("이름을 인식하지 못했어요. (이름이 한 열에 나열된 파일)"); setBusy(false); return; }
      const res = await api.post("/insurance/name-order/", { year_month: ym, names });
      setNameOrder(names);
      await loadOrderMonths();
      setMsg(`${fmtYm(ym)} 순서 ${res.data?.saved ?? names.length}명 저장 완료.`);
    } catch (err) { setMsg("순서 저장 실패: " + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };

  const importOrder = async (srcYm) => {
    if (!srcYm || !ym) return;
    setBusy(true);
    try {
      const r = await api.get("/insurance/name-order/", { params: { ym: srcYm } });
      const names = (Array.isArray(r.data) ? r.data : []).map(normName);
      await api.post("/insurance/name-order/", { year_month: ym, names });
      setNameOrder(names);
      await loadOrderMonths();
      setMsg(`${fmtYm(srcYm)} 순서를 ${fmtYm(ym)}에 가져왔습니다.`);
    } catch (err) { setMsg("순서 가져오기 실패: " + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };

  const resetOrder = async () => {
    if (!ym) return;
    if (!window.confirm(`${fmtYm(ym)} 순서를 초기화 하시겠습니까?`)) return;
    setBusy(true);
    try {
      const res = await api.post("/insurance/order/reset/", { ym });
      setNameOrder([]);
      await loadOrderMonths();
      setMsg(`${fmtYm(ym)} 순서 ${res.data?.deleted ?? 0}건 초기화 완료.`);
    } catch (err) { setMsg("순서 초기화 실패: " + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };

  // ── 부문/소속 추가 ──
  const addUnit = async () => {
    const v = window.prompt("추가할 배정부문 이름 (예: 스마트팜)");
    const key = (v || "").trim();
    if (!key) return;
    setBusy(true);
    try { await api.post("/insurance/business-units/", { key, label: key }); await loadUnits(); setMsg(`배정부문 '${key}' 추가됨.`); }
    catch (e) { setMsg("부문 추가 실패: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  const addAff = async () => {
    const v = window.prompt("추가할 소속 이름");
    const name = (v || "").trim();
    if (!name) return;
    setBusy(true);
    try { await api.post("/insurance/affiliations/", { name }); await loadAffs(); setMsg(`소속 '${name}' 추가됨.`); }
    catch (e) { setMsg("소속 추가 실패: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  // ── 집계 데이터 ──
  const shownTypes = SOURCE_TYPES[source] || INS_ORDER;

  // 메인테이블 표시 열: 고용_실업+고용_고안을 "고용" 한 칸으로 합쳐서 보여줌.
  // 각 열은 { key, label, members(합칠 실제 보험 key들) }
  const mainCols = (() => {
    const cols = [];
    let employmentPushed = false;
    shownTypes.forEach((t) => {
      if (t === "고용_실업" || t === "고용_고안") {
        if (!employmentPushed) {
          const emp = shownTypes.filter((x) => x === "고용_실업" || x === "고용_고안");
          cols.push({ key: "고용", label: "고용", members: emp });
          employmentPushed = true;
        }
      } else {
        cols.push({ key: t, label: INS_LABEL[t], members: [t] });
      }
    });
    return cols;
  })();

  const showTotalCol = mainCols.length > 1;
  const sectionColSpan = 1 + mainCols.length + (showTotalCol ? 1 : 0);


  const btEmp = summaryEmp?.by_type || {};
  const btTot = summaryTot?.by_type || {};
  const ubtEmp = {}; (summaryEmp?.unmatched || []).forEach((x) => { ubtEmp[x.ins_type] = (ubtEmp[x.ins_type] || 0) + x.amount; });
  const ubtTot = {}; (summaryTot?.unmatched || []).forEach((x) => { ubtTot[x.ins_type] = (ubtTot[x.ins_type] || 0) + x.amount; });

  // 메인테이블 행: 소속을 묶어서 표시.
  //  - KEEP_AFFS(본사/종합환경/소독방역)는 단독 행
  //  - 그 외 소속은 "현장근로자" 한 행으로 합산
  const rawGroupKeys = (summaryTot?.group_keys || summaryEmp?.group_keys || []);
  const fieldAffs = rawGroupKeys.filter((k) => !KEEP_AFFS.includes(k)); // 현장으로 묶일 소속들
  const groupRows = [
    ...KEEP_AFFS.filter((k) => rawGroupKeys.includes(k)).map((k) => ({ key: k, members: [k], label: k })),
    ...(fieldAffs.length ? [{ key: "__field__", members: fieldAffs, label: FIELD_LABEL }] : []),
  ];

  // 한 행(묶음) × 한 열(col)의 금액 = col의 보험들 × row의 소속들 합
  const cEmp = (row, col) => col.members.reduce((cs, t) => cs + row.members.reduce((s, k) => s + (btEmp[t]?.[k] || 0), 0), 0);
  const cTot = (row, col) => col.members.reduce((cs, t) => cs + row.members.reduce((s, k) => s + (btTot[t]?.[k] || 0), 0), 0);

  const cWor = (row, col) => cTot(row, col) - cEmp(row, col);
  const uEmp = (col) => col.members.reduce((s, t) => s + (ubtEmp[t] || 0), 0);
  const uTot = (col) => col.members.reduce((s, t) => s + (ubtTot[t] || 0), 0);
  const uWor = (col) => uTot(col) - uEmp(col);

  const rowSum = (row, cell) => mainCols.reduce((s, col) => s + cell(row, col), 0);
  const colSum = (col, cell, un) => groupRows.reduce((s, u) => s + cell(u, col), 0) + un(col);
  const blockGrand = (cell, un) => groupRows.reduce((s, u) => s + rowSum(u, cell), 0) + mainCols.reduce((s, col) => s + un(col), 0);
  const unmatchedRowSum = (un) => mainCols.reduce((s, col) => s + un(col), 0);

  // 이자 차감 / 정산 금액 (총액 기준, 열별). 입력값도 col.key 단위.
  const interestOf = (col) => num(interest[col.key]);
  const settleOf = (col) => colSum(col, cTot, uTot) - interestOf(col);
  const interestSum = mainCols.reduce((s, col) => s + interestOf(col), 0);
  const settleGrand = blockGrand(cTot, uTot) - interestSum;


  const hasUnmatchedEmp = mainCols.some((col) => uEmp(col) !== 0);
  const hasUnmatchedWor = mainCols.some((col) => uWor(col) !== 0);


  const loadedTypes = INS_ORDER.filter((t) => btTot[t] || btEmp[t]);
  const ymLabel = fmtYm(ym);

  const renderUnitRows = (cell, un, hasUn) => (
    <>

      {groupRows.map((u) => (
        <tr key={u.key}>
          <td style={{ ...S.td, whiteSpace: "nowrap" }}>{u.label}</td>
          {mainCols.map((col) => <td key={col.key} style={S.tdR}>{fmt(cell(u, col))}</td>)}
          {showTotalCol && <td style={{ ...S.tdR, fontWeight: 600 }}>{fmt(rowSum(u, cell))}</td>}
        </tr>
      ))}


      {hasUn && (
        <tr style={{ background: "#fef2f2" }}>
          <td style={{ ...S.td, color: "#b91c1c", fontWeight: 600 }}>⚠ 미지정</td>
          {mainCols.map((col) => <td key={col.key} style={{ ...S.tdR, color: "#b91c1c" }}>{fmt(un(col))}</td>)}
          {showTotalCol && <td style={{ ...S.tdR, color: "#b91c1c", fontWeight: 600 }}>{fmt(unmatchedRowSum(un))}</td>}
        </tr>
      )}


    </>
  );

  // ── 개별 모달: 근로구분 탭 → 검색 → 정렬 ──
  // 탭 규칙: 전체=모두 / 일용직=일용만 / 재직자=일반+미퇴직 / 퇴직자=일반+퇴직 (재직·퇴직 탭은 일용 제외)
  const tabPass = (p) => {
    const isIlyong = p.worker_type === "일용";
    if (empTab === "일용직") return isIlyong;
    if (empTab === "재직자") return !isIlyong && !p.resigned;
    if (empTab === "퇴직자") return !isIlyong && p.resigned;
    return true; // 전체
  };
  const tabbed = people.filter(tabPass);
  const q = query.trim();
  const qNum = q.replace(/[, ]/g, "");
  const filtered = !q ? tabbed : tabbed.filter((p) => {
    const unitLabel = unitLabelMap[p.unit] || "";
    const aff = p.affiliation || "";
    const un = (!p.unit || !p.affiliation) ? "미지정" : "";
    const amounts = [...INS_ORDER.map((t) => p.by_type?.[t] || 0), p.sum_emp].map(String);
    const hay = [p.name, p.birth, unitLabel, aff, un].join(" ");
    return hay.includes(q) || (qNum && amounts.some((a) => a.includes(qNum)));
  });
  const displayed = sortByOrder(filtered);
  const fSum = (t) => filtered.reduce((s, p) => s + (p.by_type?.[t] || 0), 0);
  const fSumAll = filtered.reduce((s, p) => s + (p.sum_emp || 0), 0);
  // 탭 카운트
  const cntAll = people.length;
  const cntIlyong = people.filter((p) => p.worker_type === "일용").length;
  const cntActive = people.filter((p) => p.worker_type !== "일용" && !p.resigned).length;
  const cntResigned = people.filter((p) => p.worker_type !== "일용" && p.resigned).length;
  const EMP_TABS = [
    { key: "전체", cnt: cntAll }, { key: "재직자", cnt: cntActive },
    { key: "퇴직자", cnt: cntResigned }, { key: "일용직", cnt: cntIlyong },
  ];

  // ── 매핑 모달: 검색 + 정렬 (편집 버퍼 기준) ──
  const mq = mapQuery.trim();
  const mapList = sortByOrder(!mq ? people : people.filter((p) => {
    const k = pkey(p.name, p.birth);
    const unitLabel = unitLabelMap[mapping[k]] || "";
    const aff = affMap[k] || "";
    const un = (!mapping[k] || !affMap[k]) ? "미지정" : "";
    return [p.name, p.birth, unitLabel, aff, un].join(" ").includes(mq);
  }));
  const assignedCount = people.filter((p) => mapping[pkey(p.name, p.birth)]).length;
  const affCount = people.filter((p) => affMap[pkey(p.name, p.birth)]).length;

  // ── 엑셀 다운로드 (개별 사회보험료, 사업주부담) ──
  const downloadExcel = async () => {
    if (people.length === 0) { setMsg("내보낼 데이터가 없어요."); return; }
    let XS;
    try { const mod = await import("xlsx-js-style"); XS = mod.default || mod; }
    catch { setMsg("엑셀 모듈이 없습니다. 터미널에서 'npm install xlsx-js-style' 후 다시 시도하세요."); return; }

    const headers = ["연번", "이름", "생년월일", "배정부문", "소속", ...INS_ORDER.map((t) => INS_LABEL_FULL[t]), "합계"];
    const body = displayed.map((p, i) => ([
      i + 1, p.name, p.birth,
      unitLabelMap[p.unit] || "미지정", p.affiliation || "미지정",
      ...INS_ORDER.map((t) => p.by_type?.[t] || 0), p.sum_emp || 0,
    ]));
    const totalRow = ["", "", "", "", "합계", ...INS_ORDER.map((t) => fSum(t)), fSumAll];
    const aoa = [headers, ...body, totalRow];

    const ws = XS.utils.aoa_to_sheet(aoa);
    ws["!cols"] = [{ wch: 5 }, { wch: 10 }, { wch: 11 }, { wch: 14 }, { wch: 18 }, ...INS_ORDER.map(() => ({ wch: 13 })), { wch: 14 }];

    const border = { top: { style: "thin", color: { rgb: "BBBBBB" } }, bottom: { style: "thin", color: { rgb: "BBBBBB" } }, left: { style: "thin", color: { rgb: "BBBBBB" } }, right: { style: "thin", color: { rgb: "BBBBBB" } } };
    const range = XS.utils.decode_range(ws["!ref"]);
    for (let r = range.s.r; r <= range.e.r; r++) {
      for (let c = range.s.c; c <= range.e.c; c++) {
        const addr = XS.utils.encode_cell({ r, c });
        if (!ws[addr]) ws[addr] = { t: "s", v: "" };
        const isHeader = r === 0;
        const isTotal = r === range.e.r;
        const isNum = c >= 5;
        ws[addr].s = {
          border,
          alignment: { horizontal: isNum && !isHeader ? "right" : "center", vertical: "center" },
          font: { bold: isHeader || isTotal },
          fill: isHeader ? { fgColor: { rgb: "F1F5F9" } } : (isTotal ? { fgColor: { rgb: "F8FAFC" } } : { fgColor: { rgb: "FFFFFF" } }),
        };
        if (isNum && !isHeader) ws[addr].z = "#,##0";
      }
    }

    const wb = XS.utils.book_new();
    XS.utils.book_append_sheet(wb, ws, "개별사회보험료");
    XS.writeFile(wb, `[ERP] ${ymLabel} 개별 사회보험료.xlsx`);
  };

  // ── 엑셀 다운로드 (메인테이블: 소속별 집계, 현재 화면 기준) ──
  const downloadSummaryExcel = async () => {
    if (groupRows.length === 0 && !hasUnmatchedEmp && !hasUnmatchedWor) {
      setMsg("내보낼 집계 데이터가 없어요."); return;
    }
    let XS;
    try { const mod = await import("xlsx-js-style"); XS = mod.default || mod; }
    catch { setMsg("엑셀 모듈이 없습니다. 터미널에서 'npm install xlsx-js-style' 후 다시 시도하세요."); return; }

    const basisLabel = basis === "employer" ? "사업주부담" : "총액";
    const headers = ["소속", ...mainCols.map((col) => col.label), ...(showTotalCol ? ["합계"] : [])];



    const aoa = [headers];
    const rowMeta = [];

    const pushRow = (label, vals, type) => {
      aoa.push([label, ...vals, ...(showTotalCol ? [vals.reduce((s, v) => s + v, 0)] : [])]);
      rowMeta.push(type);
    };
    const pushTotalRow = (label, cellVals, grand, type = "total") => {
      aoa.push([label, ...cellVals, ...(showTotalCol ? [grand] : [])]);
      rowMeta.push(type);
    };
    const pushSection = (label) => {
      aoa.push([label, ...mainCols.map(() => ""), ...(showTotalCol ? [""] : [])]);
      rowMeta.push("section");
    };

    if (basis === "employer") {
      groupRows.forEach((u) => pushRow(u.label, mainCols.map((col) => cEmp(u, col)), "data"));
      if (hasUnmatchedEmp) pushRow("⚠ 미지정", mainCols.map((col) => uEmp(col)), "unmatched");
      pushTotalRow("합계", mainCols.map((col) => colSum(col, cEmp, uEmp)), blockGrand(cEmp, uEmp));
    } else {
      pushSection("본인부담금 (근로자)");
      groupRows.forEach((u) => pushRow(u.label, mainCols.map((col) => cWor(u, col)), "data"));
      if (hasUnmatchedWor) pushRow("⚠ 미지정", mainCols.map((col) => uWor(col)), "unmatched");
      pushSection("기관부담금 (사업주)");
      groupRows.forEach((u) => pushRow(u.label, mainCols.map((col) => cEmp(u, col)), "data"));
      if (hasUnmatchedEmp) pushRow("⚠ 미지정", mainCols.map((col) => uEmp(col)), "unmatched");
      pushTotalRow("합계 (총액)", mainCols.map((col) => colSum(col, cTot, uTot)), blockGrand(cTot, uTot));
      pushRow("이자 차감", mainCols.map((col) => interestOf(col)), "interest");
      pushTotalRow("정산 금액", mainCols.map((col) => settleOf(col)), settleGrand, "settle");
    }



    const ws = XS.utils.aoa_to_sheet(aoa);

    ws["!cols"] = [{ wch: 16 }, ...mainCols.map(() => ({ wch: 14 })), ...(showTotalCol ? [{ wch: 15 }] : [])];




    const border = { top: { style: "thin", color: { rgb: "BBBBBB" } }, bottom: { style: "thin", color: { rgb: "BBBBBB" } }, left: { style: "thin", color: { rgb: "BBBBBB" } }, right: { style: "thin", color: { rgb: "BBBBBB" } } };
    const range = XS.utils.decode_range(ws["!ref"]);
    for (let r = range.s.r; r <= range.e.r; r++) {
      const meta = r === 0 ? "header" : rowMeta[r - 1];
      const fill =
        meta === "header" ? "F1F5F9" :
        meta === "section" ? "EEF2FF" :
        meta === "unmatched" ? "FEF2F2" :
        meta === "total" ? "F8FAFC" :
        meta === "interest" ? "FFFBEB" :
        meta === "settle" ? "F0FDF4" : "FFFFFF";
      const bold = meta === "header" || meta === "section" || meta === "total" || meta === "settle";
      for (let c = range.s.c; c <= range.e.c; c++) {
        const addr = XS.utils.encode_cell({ r, c });
        if (!ws[addr]) ws[addr] = { t: "s", v: "" };
        const isNum = c >= 1 && meta !== "header" && meta !== "section";
        ws[addr].s = {
          border,
          alignment: { horizontal: c === 0 ? "left" : (isNum ? "right" : "center"), vertical: "center" },
          font: { bold },
          fill: { fgColor: { rgb: fill } },
        };
        if (isNum && typeof ws[addr].v === "number") ws[addr].z = "#,##0";
      }
    }

    const wb = XS.utils.book_new();
    XS.utils.book_append_sheet(wb, ws, "소속별집계");
    XS.writeFile(wb, `[ERP] ${ymLabel} 소속별 집계(${basisLabel}).xlsx`);
  };

  const S = {



    wrap: { padding: 16, fontSize: 13, color: "#1f2937" },
    bar: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 12 },
    chip: (a) => ({
      padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontWeight: 600,
      border: a ? "1px solid #2563eb" : "1px solid #d1d5db", background: a ? "#2563eb" : "#fff", color: a ? "#fff" : "#374151"
    }),
    btn: (c, d) => ({
      width: "auto", padding: "6px 12px", background: d ? "#9ca3af" : c, color: "#fff",
      border: "none", borderRadius: 6, fontWeight: 600, cursor: d ? "not-allowed" : "pointer", fontSize: 12, whiteSpace: "nowrap"
    }),
    btnSm: (c, d) => ({
      width: "auto", padding: "4px 10px", background: d ? "#9ca3af" : c, color: "#fff",
      border: "none", borderRadius: 6, fontWeight: 600, cursor: d ? "not-allowed" : "pointer", fontSize: 12, whiteSpace: "nowrap", flex: "0 0 auto"
    }),
    th: { background: "#f1f5f9", padding: "6px 8px", borderBottom: "1px solid #cbd5e1", textAlign: "left", whiteSpace: "nowrap" },
    thR: { background: "#f1f5f9", padding: "6px 8px", borderBottom: "1px solid #cbd5e1", textAlign: "right", whiteSpace: "nowrap" },
    td: { padding: "5px 8px", borderBottom: "1px solid #eef2f7" },
    tdR: { padding: "5px 8px", borderBottom: "1px solid #eef2f7", textAlign: "right" },
    sel: { padding: "3px 6px", borderRadius: 4, border: "1px solid #cbd5e1" },
    input: { padding: "4px 8px", borderRadius: 6, border: "1px solid #cbd5e1" },
    inputCell: { width: "100%", padding: "3px 6px", borderRadius: 4, border: "1px solid #cbd5e1", boxSizing: "border-box" },
    modalWrap: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 },
    modalCard: (w) => ({ background: "#fff", borderRadius: 10, width: `min(${w}px, 96vw)`, maxHeight: "88vh", display: "flex", flexDirection: "column", boxShadow: "0 12px 40px rgba(0,0,0,0.25)", overflow: "hidden" }),
    modalHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "12px 16px", borderBottom: "1px solid #e2e8f0", flexWrap: "wrap" },
  };

  const unassignedCell = (val) => (
    <span style={{ color: val ? "#1f2937" : "#b91c1c", fontWeight: val ? 400 : 600 }}>{val || "미지정"}</span>
  );

  return (
    <div style={S.wrap}>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>사회보험 부문 구분</h2>
      <p style={{ color: "#6b7280", marginBottom: 14 }}>
        파일을 올리면 <b>연월·종류 자동 인식</b> 후 즉시 저장. 매핑·순서는 <b>연월별</b>로 관리됩니다.
      </p>

      <div style={S.bar}>
        <label style={{ whiteSpace: "nowrap" }}>연월</label>
        <select value={ym.slice(0, 4)} onChange={(e) => setYm(`${e.target.value}-${ym.slice(5, 7) || "01"}`)} style={{ ...S.sel, width: 90 }}>
          <option value="">년</option>
          {YEARS.map((y) => <option key={y} value={y}>{y}년</option>)}
        </select>
        <select value={ym.slice(5, 7)} onChange={(e) => setYm(`${ym.slice(0, 4) || NOW_Y}-${e.target.value}`)} style={{ ...S.sel, width: 70 }}>
          <option value="">월</option>
          {MONTHS.map((m) => <option key={m} value={m}>{+m}월</option>)}
        </select>

        <div style={{ display: "flex", gap: 4, marginLeft: 8 }}>
          {SOURCES.map((s) => <div key={s} style={S.chip(source === s)} onClick={() => setSource(s)}>{s}</div>)}
        </div>
        <label style={{ ...S.btn("#0ea5e9", busy), display: "inline-block", opacity: busy ? 0.6 : 1 }}>
          📂 {source === "전체" ? "파일 업로드 (자동인식)" : `${source} 업로드`}
          <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} disabled={busy} style={{ display: "none" }} />
        </label>

        <span style={{ marginLeft: 12, whiteSpace: "nowrap" }}>기준:</span>
        <div style={{ display: "flex", gap: 4 }}>
          <div style={S.chip(basis === "total")} onClick={() => setBasis("total")}>총액</div>
          <div style={S.chip(basis === "employer")} onClick={() => setBasis("employer")}>사업주부담</div>
        </div>

        <button style={{ ...S.btnSm("#dc2626", busy), marginLeft: "auto" }} disabled={busy} onClick={deleteMonth}>🗑 이 달 삭제</button>
      </div>

      {loadedTypes.length > 0 && (
        <div style={{ marginBottom: 10, color: "#334155", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span>저장된 보험: {loadedTypes.map((t) => INS_LABEL[t]).join(", ")} · 인원 {people.length}명</span>
          <button style={S.btnSm("#7c3aed", false)} onClick={() => setShowOrderModal(true)}>↕ {ymLabel} 개별 순서 설정</button>
          <button style={S.btnSm("#0891b2", false)} onClick={() => setShowMapModal(true)} disabled={people.length === 0}>🧩 {ymLabel} 개별 매핑 설정</button>
          <button style={S.btnSm("#6366f1", false)} onClick={() => setShowModal(true)} disabled={people.length === 0}>👤 {ymLabel} 개별 사회보험료 확인</button>
          {source !== "전체" && <span style={{ color: "#2563eb" }}>· 현재 표시: {shownTypes.map((t) => INS_LABEL[t]).join(", ")}</span>}
        </div>
      )}

      {msg && (
        <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", padding: "6px 10px", borderRadius: 6, marginBottom: 12, color: "#075985" }}>{msg}</div>
      )}

      {/* 부문별 집계 */}
      <div style={{ maxWidth: 1012 }}> {/* 메인테이블 총 크기*/}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
          <b style={{ whiteSpace: "nowrap" }}>
            소속별 집계 — {ymLabel} ({basis === "employer" ? "사업주부담" : "총액"}{source !== "전체" ? ` / ${source}` : ""})
          </b>
          <div style={{ display: "flex", gap: 6 }}>
            <button style={S.btnSm("#16a34a", busy)} disabled={busy} onClick={saveInterest}>💾 이자 차감 저장</button>
            <button style={S.btnSm("#15803d", busy)} disabled={busy} onClick={downloadSummaryExcel}>📥 집계 엑셀</button>
            <button style={S.btnSm("#0ea5e9", busy)} disabled={busy} onClick={() => loadSummary(ym)}>새로고침</button>
          </div>


        </div>
        <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 8 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...S.th, width: 140, whiteSpace: "nowrap" }}>소속</th>
                {mainCols.map((col) => <th key={col.key} style={S.thR}>{col.label}</th>)}
                {showTotalCol && <th style={S.thR}>합계</th>}


              </tr>
            </thead>


            {basis === "employer" ? (
              <tbody>
                {renderUnitRows(cEmp, uEmp, hasUnmatchedEmp)}
                <tr style={{ background: "#f8fafc", fontWeight: 700 }}>
                  <td style={S.td}>합계</td>
                  {mainCols.map((col) => <td key={col.key} style={S.tdR}>{fmt(colSum(col, cEmp, uEmp))}</td>)}
                  {showTotalCol && <td style={S.tdR}>{fmt(blockGrand(cEmp, uEmp))}</td>}
                </tr>


              </tbody>




            ) : (
              <tbody>
                <tr style={{ background: "#eef2ff" }}>
                  <td style={{ ...S.td, fontWeight: 700, color: "#3730a3" }} colSpan={sectionColSpan}>본인부담금 (근로자)</td>
                </tr>
                {renderUnitRows(cWor, uWor, hasUnmatchedWor)}
                <tr style={{ background: "#ecfeff" }}>
                  <td style={{ ...S.td, fontWeight: 700, color: "#155e75" }} colSpan={sectionColSpan}>기관부담금 (사업주)</td>
                </tr>
                {renderUnitRows(cEmp, uEmp, hasUnmatchedEmp)}

                <tr style={{ background: "#f8fafc", fontWeight: 700 }}>
                  <td style={S.td}>합계 (총액)</td>
                  {mainCols.map((col) => <td key={col.key} style={S.tdR}>{fmt(colSum(col, cTot, uTot))}</td>)}
                  {showTotalCol && <td style={S.tdR}>{fmt(blockGrand(cTot, uTot))}</td>}
                </tr>
                <tr style={{ background: "#fffbeb" }}>
                  <td style={S.td}>이자 차감</td>
                  {mainCols.map((col) => (
                    <td key={col.key} style={{ ...S.tdR, padding: "3px 4px" }}>
                      <input
                        type="text"
                        value={interest[col.key] ?? ""}
                        onChange={(e) => setInterest((p) => ({ ...p, [col.key]: e.target.value }))}
                        placeholder="0"
                        style={{ ...S.inputCell, textAlign: "right" }}
                      />
                    </td>
                  ))}
                  {showTotalCol && <td style={{ ...S.tdR, fontWeight: 600 }}>{fmt(interestSum)}</td>}
                </tr>
                <tr style={{ background: "#f0fdf4", fontWeight: 700 }}>
                  <td style={S.td}>정산 금액</td>
                  {mainCols.map((col) => <td key={col.key} style={S.tdR}>{fmt(settleOf(col))}</td>)}
                  {showTotalCol && <td style={S.tdR}>{fmt(settleGrand)}</td>}
                </tr>


              </tbody>
            )}
          </table>


        </div>
        {months.length > 0 && <p style={{ color: "#94a3b8", fontSize: 12, marginTop: 8 }}>적재된 연월: {months.join(", ")}</p>}
      </div>

      {/* ── 개별 사회보험료 ── */}
      {showModal && (
        <div onClick={() => setShowModal(false)} style={S.modalWrap}>
          <div onClick={(e) => e.stopPropagation()} style={S.modalCard(1100)}>
            <div style={S.modalHead}>
              <b style={{ fontSize: 15 }}>개별 사회보험료 — {ymLabel} (사업주부담)</b>
              <div style={{ display: "flex", gap: 8, flex: "0 0 auto" }}>
                <button style={S.btnSm("#16a34a", busy)} disabled={busy} onClick={downloadExcel}>📥 엑셀 다운로드</button>
                <button style={S.btnSm("#64748b", false)} onClick={() => setShowModal(false)}>닫기 ✕</button>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", flexWrap: "wrap" }}>
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="검색: 이름 / 생년월일 / 배정명 / 소속명 / 미지정 / 금액" style={{ ...S.input, width: 380 }} />
              <div style={{ display: "flex", gap: 4 }}>
                {EMP_TABS.map((t) => (
                  <div key={t.key} style={S.chip(empTab === t.key)} onClick={() => setEmpTab(t.key)}>
                    {t.key} {t.cnt}
                  </div>
                ))}
              </div>
              {q && <span style={{ color: "#64748b" }}>{filtered.length}명 검색됨</span>}
              {q && <button style={S.btnSm("#64748b", false)} onClick={() => setQuery("")}>지우기</button>}
            </div>
            <div style={{ overflow: "auto", padding: "0 16px 16px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ position: "sticky", top: 0 }}>
                  <tr>
                    <th style={S.th}>연번</th>
                    <th style={S.th}>이름</th>
                    <th style={S.th}>생년월일</th>
                    <th style={S.th}>배정부문</th>
                    <th style={S.th}>소속</th>
                    {INS_ORDER.map((t) => <th key={t} style={S.thR}>{INS_LABEL_FULL[t]}</th>)}
                    <th style={S.thR}>합계</th>
                  </tr>
                </thead>
                <tbody>
                  {displayed.map((p, idx) => {
                    const k = pkey(p.name, p.birth); const no = !p.unit;
                    return (
                      <tr key={k} style={no ? { background: "#fef2f2" } : {}}>
                        <td style={{ ...S.td, color: "#94a3b8" }}>{idx + 1}</td>
                        <td style={{ ...S.td, fontWeight: 600 }}>{p.name}</td>
                        <td style={{ ...S.td, color: "#64748b" }}>{p.birth}</td>
                        <td style={S.td}>{unassignedCell(unitLabelMap[p.unit])}</td>
                        <td style={S.td}>{unassignedCell(p.affiliation)}</td>
                        {INS_ORDER.map((t) => <td key={t} style={S.tdR}>{fmt(p.by_type?.[t] || 0)}</td>)}
                        <td style={{ ...S.tdR, fontWeight: 600 }}>{fmt(p.sum_emp)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background: "#f8fafc", fontWeight: 700 }}>
                    <td style={S.td} colSpan={5}>합계 ({filtered.length}명{q ? " · 검색" : ""})</td>
                    {INS_ORDER.map((t) => <td key={t} style={S.tdR}>{fmt(fSum(t))}</td>)}
                    <td style={S.tdR}>{fmt(fSumAll)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── 매핑 설정 ── */}
      {showMapModal && (
        <div onClick={() => setShowMapModal(false)} style={S.modalWrap}>
          <div onClick={(e) => e.stopPropagation()} style={S.modalCard(880)}>
            <div style={S.modalHead}>
              <b style={{ fontSize: 15 }}>{ymLabel} 개별 매핑 설정 (배정부문 · 소속)</b>
              <div style={{ display: "flex", gap: 8, flex: "0 0 auto" }}>
                <button style={S.btnSm("#16a34a", busy)} disabled={busy} onClick={saveMapping}>저장</button>
                <button style={S.btnSm("#64748b", false)} onClick={() => setShowMapModal(false)}>닫기 ✕</button>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", flexWrap: "wrap" }}>
              <select value="" onChange={(e) => { if (e.target.value) importMapping(e.target.value); e.target.value = ""; }} style={S.sel}>
                <option value="">저장된 db 가져오기</option>
                {mappingMonths.map((m) => <option key={m} value={m}>{fmtYm(m)} 매핑 설정</option>)}
              </select>
              <button style={S.btnSm("#0891b2", busy)} disabled={busy} onClick={addUnit}>+ 부문 추가</button>
              <button style={S.btnSm("#0891b2", busy)} disabled={busy} onClick={addAff}>+ 소속 추가</button>



              <span style={{ color: "#475569" }}>현재 배정 {assignedCount}명 · 소속 {affCount}명</span>


              <button style={S.btnSm("#dc2626", busy)} disabled={busy} onClick={resetMapping}>🗑 {ymLabel} 매핑 초기화</button>
              <input value={mapQuery} onChange={(e) => setMapQuery(e.target.value)} placeholder="검색: 이름/생년월일/배정명/소속명/미지정" style={{ ...S.input, width: 220, marginLeft: "auto" }} />
              {mq && <button style={S.btnSm("#64748b", false)} onClick={() => setMapQuery("")}>지우기</button>}
            </div>
            <div style={{ overflow: "auto", padding: "0 16px 16px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ position: "sticky", top: 0 }}>
                  <tr>
                    <th style={S.th}>연번</th>
                    <th style={S.th}>이름</th>
                    <th style={S.th}>생년월일</th>
                    <th style={{ ...S.th, width: 210 }}>배정부문</th>
                    <th style={{ ...S.th, width: 210 }}>소속</th>
                  </tr>
                </thead>
                <tbody>
                  {mapList.map((p, idx) => {
                    const k = pkey(p.name, p.birth); const no = !mapping[k];
                    return (
                      <tr key={k} style={no ? { background: "#fef2f2" } : {}}>
                        <td style={{ ...S.td, color: "#94a3b8" }}>{idx + 1}</td>
                        <td style={{ ...S.td, fontWeight: 600 }}>{p.name}</td>
                        <td style={{ ...S.td, color: "#64748b" }}>{p.birth}</td>
                        <td style={S.td}>
                          <select value={mapping[k] || ""} onChange={(e) => setUnit(k, e.target.value)} style={{ ...S.inputCell, borderColor: no ? "#ef4444" : "#cbd5e1" }}>
                            <option value="">미지정</option>
                            {units.map((u) => <option key={u.key} value={u.key}>{u.label}</option>)}
                          </select>
                        </td>
                        <td style={S.td}>
                          <select value={affMap[k] || ""} onChange={(e) => setAff(k, e.target.value)} style={S.inputCell}>
                            <option value="">미지정</option>
                            {affs.map((a) => <option key={a} value={a}>{a}</option>)}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── 순서 설정 ── */}
      {showOrderModal && (
        <div onClick={() => setShowOrderModal(false)} style={S.modalWrap}>
          <div onClick={(e) => e.stopPropagation()} style={S.modalCard(560)}>
            <div style={S.modalHead}>
              <b style={{ fontSize: 15 }}>{ymLabel} 개별 순서 설정</b>
              <button style={S.btnSm("#64748b", false)} onClick={() => setShowOrderModal(false)}>닫기 ✕</button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", flexWrap: "wrap" }}>
              <select value="" onChange={(e) => { if (e.target.value) importOrder(e.target.value); e.target.value = ""; }} style={S.sel}>
                <option value="">저장된 db 가져오기</option>
                {orderMonths.map((m) => <option key={m} value={m}>{fmtYm(m)} 순서</option>)}
              </select>
              <label style={{ ...S.btnSm("#0ea5e9", busy), display: "inline-block", opacity: busy ? 0.6 : 1 }}>
                📂 순서 파일 업로드
                <input type="file" accept=".xlsx,.xls,.csv" onChange={handleOrderFile} disabled={busy} style={{ display: "none" }} />
              </label>
              
              <span style={{ color: "#475569" }}>
                현재 {nameOrder.length}명 · 중복 {nameOrder.length - new Set(nameOrder).size}명 · 실제 {new Set(nameOrder).size}명
              </span>


              <button style={S.btnSm("#dc2626", busy)} disabled={busy} onClick={resetOrder}>🗑 {ymLabel} 순서 초기화</button>
              <span style={{ color: "#94a3b8", fontSize: 12, flexBasis: "100%" }}>
                * 다른 달 순서를 가져오거나 파일을 올리면 {ymLabel} 순서로 저장됩니다. 목록에 없는 사람은 뒤쪽에 가나다순으로 표시돼요.
              </span>
            </div>
            <div style={{ overflow: "auto", padding: "0 16px 16px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ position: "sticky", top: 0 }}>
                  <tr><th style={{ ...S.th, width: 70 }}>연번</th><th style={S.th}>이름</th></tr>
                </thead>
                <tbody>
                  {nameOrder.length === 0 ? (
                    <tr><td style={S.td} colSpan={2}>등록된 순서가 없습니다. 가져오기 또는 업로드 하세요.</td></tr>
                  ) : (
                    nameOrder.map((nm, idx) => (
                      <tr key={`${nm}-${idx}`}>
                        <td style={{ ...S.td, color: "#94a3b8" }}>{idx + 1}</td>
                        <td style={{ ...S.td, fontWeight: 600 }}>{nm}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}